import { Link } from "react-router-dom";
import { MapPin, Phone, Printer, Mail, Facebook, Linkedin } from "lucide-react";
import { SITE } from "@/lib/site";

export default function Footer() {
  return (
    <footer data-testid="site-footer" className="relative bg-navy-800 text-cream-100 mt-24">
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-gold-500/60 to-transparent" />
      <div className="container-narrow py-16 grid grid-cols-1 md:grid-cols-12 gap-12">
        <div className="md:col-span-4">
          <div className="mb-6">
            <img src="/images/logo-white.png" alt="St. John Financial Group" className="h-36 md:h-44 w-auto object-contain -ml-3" style={{ mixBlendMode: "screen" }} />
            <div className="font-serif-display text-lg text-white font-semibold mt-2">St. John Accounting & Tax</div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-cream-100/65">Owasso · Tulsa</div>
          </div>
          <p className="text-cream-100/80 leading-relaxed text-[14.5px] max-w-sm">Trusted accounting and tax services for small businesses, families, and individuals across Owasso, Tulsa, and the surrounding communities.</p>
          <div className="mt-6 flex items-center gap-3" data-testid="footer-social">
            <a href={SITE.social.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook" data-testid="footer-facebook" className="h-9 w-9 grid place-items-center rounded-full bg-white/8 hover:bg-white/15 transition-colors"><Facebook size={16} /></a>
            <a href={SITE.social.linkedin} target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" data-testid="footer-linkedin" className="h-9 w-9 grid place-items-center rounded-full bg-white/8 hover:bg-white/15 transition-colors"><Linkedin size={16} /></a>
          </div>
        </div>

        <div className="md:col-span-3">
          <h4 className="font-serif-display text-white text-base font-semibold mb-5">Site</h4>
          <ul className="space-y-3 text-[14.5px]">
            {[{ to: "/", label: "Home" }, { to: "/services", label: "What We Do" }, { to: "/team", label: "Meet Our Team" }, { to: "/resources", label: "Resources" }, { to: "/contact", label: "Contact" }, { to: "/privacy", label: "Privacy Notice" }, { to: "/disclaimer", label: "Transparency Statement" }].map((l) => (
              <li key={l.to}><Link to={l.to} data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`} className="text-cream-100/85 hover:text-white transition-colors">{l.label}</Link></li>
            ))}
            <li><a href={SITE.bookingUrl} target="_blank" rel="noopener noreferrer" data-testid="footer-book-link" className="text-cream-100/85 hover:text-white transition-colors">Book An Appointment</a></li>
          </ul>
        </div>

        <div className="md:col-span-3">
          <h4 className="font-serif-display text-white text-base font-semibold mb-5">Office</h4>
          <ul className="space-y-3 text-[14.5px] text-cream-100/85">
            <li className="flex gap-3">
              <MapPin size={18} className="text-gold-300 mt-0.5 shrink-0" />
              <a href={SITE.address.mapUrl} target="_blank" rel="noopener noreferrer" data-testid="footer-address" className="hover:text-white transition-colors">{SITE.address.street}<br />{SITE.address.cityStateZip}</a>
            </li>
            <li className="flex gap-3"><Phone size={18} className="text-gold-300 mt-0.5 shrink-0" /><a href={SITE.phoneTel} data-testid="footer-phone" className="hover:text-white transition-colors">{SITE.phone}</a></li>
            <li className="flex gap-3"><Printer size={18} className="text-gold-300 mt-0.5 shrink-0" /><span>Fax: {SITE.fax}</span></li>
            <li className="flex gap-3"><Mail size={18} className="text-gold-300 mt-0.5 shrink-0" /><a href={`mailto:${SITE.email}`} data-testid="footer-email" className="hover:text-white transition-colors break-all">{SITE.email}</a></li>
          </ul>
        </div>

        <div className="md:col-span-2">
          <h4 className="font-serif-display text-white text-base font-semibold mb-5">Related Services</h4>
          <ul className="space-y-3 text-[13px]">
            {SITE.otherCompanies.map((c) => (
              <li key={c.name}>
                <a href={c.url} target="_blank" rel="noopener noreferrer" className="text-cream-100/80 hover:text-white transition-colors">
                  <div>{c.name}</div>
                  <div className="text-cream-100/55 text-[11.5px] mt-0.5">{c.desc}</div>
                </a>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="container-narrow py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-[12.5px] text-cream-100/65">
          <div className="flex flex-col gap-1">
            <div data-testid="footer-copyright">© {new Date().getFullYear()} St. John Financial Group, Inc. · Accounting & Tax department · All rights reserved.</div>
            <div data-testid="footer-bda" className="text-cream-100/50">St. John Accounting DBA St. John Financial Group, Inc.</div>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/disclaimer" data-testid="footer-disclaimer-link" className="hover:text-white transition-colors">Transparency Statement</Link>
            <Link to="/privacy" data-testid="footer-privacy-link" className="hover:text-white transition-colors">Privacy</Link>
            <a href="https://builtby-ab.com" target="_blank" rel="noopener noreferrer" className="text-cream-100/40 tracking-wide hover:text-white transition-colors" data-testid="footer-builtby">Built by AB</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
